import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { shopify } from "@/lib/api";
import { useStore, formatPrice } from "@/lib/store";
import { t } from "@/lib/i18n";
import { Plus } from "lucide-react";
import { optimizeImg } from "@/lib/img";

/**
 * "Bundle & save" strip on PDP. Shows the strongest complementary product
 * (opposite family, available, color-matched first) with a size picker and a
 * single "Legg begge i handlekurven" button. The 10 % discount itself is
 * applied by a Shopify Automatic Discount rule ("Buy hoodie + cap → -10 %")
 * that the merchant configures once in Shopify Admin → Discounts. That way
 * the price truth stays on Shopify's checkout, not in this frontend.
 */
export default function BundleOffer({ product, currentVariantId }) {
  const { addMultipleToCart } = useStore();
  const { data: partner } = useQuery({
    queryKey: ["bundle-partner", product?.handle],
    queryFn: () => shopify.bundlePartner(product.handle),
    enabled: !!product?.handle,
    staleTime: 60_000,
  });

  const [partnerVariantId, setPartnerVariantId] = useState(null);

  useMemo(() => {
    if (partner?.default_variant_id) setPartnerVariantId(partner.default_variant_id);
  }, [partner?.default_variant_id]);

  if (!partner || !partner.available) return null;

  const chosenPartnerVariant = partner.variants?.find((v) => v.id === partnerVariantId);
  const canAddBoth = !!currentVariantId && !!chosenPartnerVariant?.available;

  const totalNok =
    parseFloat(product.price_range.min.amount) + parseFloat(partner.price.amount);
  const discountedNok = totalNok * 0.9;

  const handleAddBoth = () => {
    if (!canAddBoth) return;
    addMultipleToCart([
      { variant_id: currentVariantId, quantity: 1 },
      { variant_id: partnerVariantId, quantity: 1 },
    ]);
  };

  return (
    <section
      className="mt-10 border border-[color:var(--marcini-border)] bg-[color:var(--marcini-bg-warm)] p-5 md:p-6"
      data-testid="bundle-offer"
    >
      <div className="flex items-baseline justify-between mb-4">
        <p className="text-eyebrow">{t.pdp.bundleEyebrow}</p>
        <span className="text-eyebrow text-[color:var(--marcini-fg-3)]">−10 %</span>
      </div>
      <h3 className="font-serif-editorial text-2xl md:text-3xl leading-tight mb-5">
        {t.pdp.bundleTitle}
      </h3>

      <div className="flex items-center gap-3 md:gap-4">
        <img
          src={optimizeImg(product.images[0], 200)}
          alt=""
          className="w-16 h-20 md:w-20 md:h-24 object-cover flex-shrink-0"
        />
        <Plus size={16} className="text-[color:var(--marcini-fg-3)] flex-shrink-0" />
        <img
          src={optimizeImg(partner.thumbnail, 200)}
          alt={partner.color_name}
          className="w-16 h-20 md:w-20 md:h-24 object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="font-serif-editorial text-lg leading-tight truncate" data-testid="bundle-partner-title">
            {partner.color_name || partner.title}
          </p>
          <p className="text-eyebrow text-[color:var(--marcini-fg-3)] mt-1">
            {formatPrice(partner.price)}
          </p>
        </div>
      </div>

      {partner.variants?.length > 1 && (
        <div className="mt-5">
          <p className="text-eyebrow text-[color:var(--marcini-fg-3)] mb-2">
            {(() => {
              const s = `${partner.title || ""} ${partner.handle || ""}`.toLowerCase();
              if (s.includes("hoodie") || s.includes("genser")) return "Str. genser";
              if (s.includes("cap")) return "Str. cap";
              return "Størrelse";
            })()}
          </p>
          <div className="flex flex-wrap gap-2" data-testid="bundle-variants">
            {partner.variants.map((v) => {
              const selected = v.id === partnerVariantId;
              return (
                <button
                  key={v.id}
                  data-testid={`bundle-variant-${v.title}`}
                  disabled={!v.available}
                  onClick={() => setPartnerVariantId(v.id)}
                  className={`px-3 py-2 border text-sm min-h-[40px] ${
                    selected ? "border-black bg-black text-white" : "border-[color:var(--marcini-border)]"
                  } ${!v.available ? "text-[color:var(--marcini-fg-3)] line-through cursor-not-allowed" : "hover:border-black"}`}
                >
                  {v.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-baseline justify-between">
        <div>
          <p className="text-eyebrow text-[color:var(--marcini-fg-3)]">Totalt</p>
          <p className="text-lg" data-testid="bundle-total">
            <span className="line-through text-[color:var(--marcini-fg-3)] mr-2">
              {Math.round(totalNok).toLocaleString("no-NO")} kr
            </span>
            <span className="font-medium">
              {Math.round(discountedNok).toLocaleString("no-NO")} kr
            </span>
          </p>
        </div>
      </div>

      <button
        onClick={handleAddBoth}
        disabled={!canAddBoth}
        className={`mt-4 w-full py-3 md:py-4 text-eyebrow transition-colors ${
          canAddBoth
            ? "bg-black text-white hover:bg-black/85"
            : "bg-[color:var(--marcini-border)] text-[color:var(--marcini-fg-3)] cursor-not-allowed"
        }`}
        data-testid="bundle-add-both"
      >
        {canAddBoth ? t.pdp.bundleAddBoth : t.pdp.bundleSelectSize}
      </button>

      <p className="mt-3 text-xs text-[color:var(--marcini-fg-3)]">
        {t.pdp.bundleNote}
      </p>
    </section>
  );
}
